// api/departments.ts
import { apiClient } from './client'
import type {
  ApiDepartment,
  CreateDepartmentRequest,
  UpdateDepartmentRequest,
  MoveDepartmentRequest,
  DepartmentUser
} from '@/types/department'

// ============================================
// 部门管理 API
// ============================================
export const departmentsApi = {
  // 获取部门树
  getTree: async (): Promise<ApiDepartment[]> => {
    const response = await apiClient.get<ApiDepartment[]>('/departments')
    return response.data
  },

  // 获取当前用户可筛选部门树
  getSelectableTree: async (): Promise<ApiDepartment[]> => {
    const response = await apiClient.get<ApiDepartment[]>('/departments/selectable-tree')
    return response.data
  },

  // 获取部门详情
  getById: async (id: number): Promise<ApiDepartment> => {
    const response = await apiClient.get<ApiDepartment>(`/departments/${id}`)
    return response.data
  },

  // 获取部门子树
  getSubtree: async (id: number, maxDepth?: number): Promise<ApiDepartment> => {
    const params = maxDepth ? `?max_depth=${maxDepth}` : ''
    const response = await apiClient.get<ApiDepartment>(`/departments/${id}/subtree${params}`)
    return response.data
  },

  // 创建部门
  create: async (data: CreateDepartmentRequest): Promise<ApiDepartment> => {
    const response = await apiClient.post<ApiDepartment>('/departments', data)
    return response.data
  },

  // 更新部门
  update: async (id: number, data: UpdateDepartmentRequest): Promise<void> => {
    await apiClient.put(`/departments/${id}`, data)
  },

  // 删除部门
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/departments/${id}`)
  },

  // 移动部门
  move: async (id: number, data: MoveDepartmentRequest): Promise<void> => {
    await apiClient.put(`/departments/${id}/move`, data)
  },

  // 获取部门用户列表
  getUsers: async (departmentId: number): Promise<DepartmentUser[]> => {
    const response = await apiClient.get<DepartmentUser[]>(`/departments/${departmentId}/users`)
    return response.data
  },

  // 添加用户到部门
  addUsers: async (departmentId: number, userIds: number[]): Promise<void> => {
    await apiClient.post(`/departments/${departmentId}/users`, { user_ids: userIds })
  },

  // 移除部门用户
  removeUser: async (departmentId: number, userId: number): Promise<void> => {
    await apiClient.delete(`/departments/${departmentId}/users/${userId}`)
  },

  // 更新部门收款码
  updateQrcode: async (departmentId: number, url: string): Promise<void> => {
    await apiClient.put(`/departments/${departmentId}/qrcode`, { payment_qrcode: url })
  },
}

// 导出函数形式以保持兼容
export const getDepartmentTree = departmentsApi.getTree
export const getSelectableDepartmentTree = departmentsApi.getSelectableTree
export const getDepartment = departmentsApi.getById
export const createDepartment = departmentsApi.create
export const updateDepartment = departmentsApi.update
export const deleteDepartment = departmentsApi.delete
export const moveDepartment = departmentsApi.move
export const getDepartmentUsers = departmentsApi.getUsers
export const addUsersToDepartment = departmentsApi.addUsers
export const removeUserFromDepartment = departmentsApi.removeUser
